import { URLS } from '@/constants';
import type {
  CustomerFeedback,
  ProductCategory,
  ProductInventoryInsight,
  SkuTier,
} from '@/services/api/models/inventoryInsights';
import { apiSession, apiSlugSession } from '@/services/session';
import JSONTransform from '@/utils/json-transform';

import { NegotiationMessageRequest, NegotiationMessageResponse } from './models/negotiationMessage';

export type TypeOfDate = 'today' | 'yesterday' | 'last_week' | 'last_month' | 'end_of_last_month';

export interface InventoryInsightParams {
  brandIds?: string[] | number[];
  brandNames?: string[];
  category?: ProductCategory;
  report?: boolean;
  search?: string;
  isBottom20Pct?: boolean;
  sortBy?: string;
  typeOfDate?: TypeOfDate;
  skuTier?: SkuTier[];
}

export interface TypeOfDateFilter {
  name: string;
  value: TypeOfDate;
}

export interface BrandFilter {
  name: string;
}

export interface InventoryInsightsResponse {
  data: {
    buyerProductInsights: {
      buyerId: number;
      filters: {
        typeOfDate?: Array<TypeOfDateFilter>;
        brands?: Array<BrandFilter>;
      };
      lastUpdatedAt?: string;
      oldestLastUpdatedAt?: string;
      productInsights: Array<ProductInventoryInsight>;
      typeOfDate: TypeOfDate;
      updatedExtractionDate: string | null;
    };
  };
}

function removeDuplicateInsights(
  insights: InventoryInsightsResponse['data']['buyerProductInsights']['productInsights'],
) {
  const entries = new Map();

  insights.forEach(({ marketplaceProductId }) => {
    if (marketplaceProductId && !entries.has(marketplaceProductId)) {
      entries.set(marketplaceProductId, 1);
    } else if (marketplaceProductId && entries.has(marketplaceProductId)) {
      entries.set(marketplaceProductId, entries.get(marketplaceProductId) + 1);
    }
  });

  return insights.filter(({ marketplaceProductId }) => marketplaceProductId && entries.get(marketplaceProductId) === 1);
}

export async function getProductInsights(
  params: InventoryInsightParams,
): Promise<InventoryInsightsResponse['data']['buyerProductInsights']> {
  const paramsFormatted = new JSONTransform({
    ...params,
    brandNames: params.brandNames?.join(','),
    brandIds: params.brandIds?.join(','),
    errorFormat: 'jsonapi',
  }).toSnakeCaseKeys();
  const response = await apiSession.get<InventoryInsightsResponse>(URLS.CATEGORY_MANAGEMENT.INSIGHTS, {
    params: { ...paramsFormatted },
  });

  if (!params.report) {
    return {
      ...response.data.data.buyerProductInsights,
      ...(Array.isArray(response.data.data.buyerProductInsights?.productInsights)
        ? { productInsights: removeDuplicateInsights(response.data.data.buyerProductInsights.productInsights) }
        : {}),
    };
  }

  return response.data.data.buyerProductInsights;
}

export async function sendCustomerFeedback(params: CustomerFeedback) {
  const paramsFormatted = {
    ...params,
    issue_type: params.issueType,
  };

  await apiSlugSession.post(URLS.CATEGORY_MANAGEMENT.CUSTOMER_FEEDBACK, paramsFormatted);
}

export async function sendNegotiationMessage(params: NegotiationMessageRequest): Promise<NegotiationMessageResponse> {
  const response = await apiSlugSession.post(URLS.CATEGORY_MANAGEMENT.NEGOTIATE, params);
  return response.data;
}
